import dchAPI from '@/services/dchAPI';
import { schedulesPayload } from '@/services/taskSchedules';

// Schedules of data tasks, for superadmins.
export const TASK_CONFIG_PATH = 'tasks/config';

const state = {
  availableTasks: [],
  taskRuns: [],
  activeRun: null,
  isLoadingTasks: false,
  isLoadingRuns: false,
  runsCursor: null,
  taskConfigs: [],
};

const getters = {
  availableTasks: (state) => state.availableTasks,
  taskRuns: (state) => state.taskRuns,
  activeRun: (state) => state.activeRun,
  isLoadingTasks: (state) => state.isLoadingTasks,
  isLoadingRuns: (state) => state.isLoadingRuns,
  hasMoreRuns: (state) => state.runsCursor !== null,
  taskConfigs: (state) => state.taskConfigs,
};

const actions = {
  async fetchTaskConfigs({ commit }) {
    const response = await dchAPI.get(TASK_CONFIG_PATH);
    commit('setTaskConfigs', response.data.data);
    return response.data.data;
  },

  // Saves a task's whole schedule list: creates its config, replaces it, or deletes it with the last schedule.
  async saveTaskSchedules({ state, commit }, { task_id, schedules }) {
    const exists = state.taskConfigs.some((config) => config.task_id === task_id);
    if (schedules.length === 0) {
      if (exists) await dchAPI.delete(`${TASK_CONFIG_PATH}/${task_id}`);
      commit('removeTaskConfig', task_id);
      return null;
    }
    const response = exists
      ? await dchAPI.put(`${TASK_CONFIG_PATH}/${task_id}`, { schedules: schedulesPayload(schedules) })
      : await dchAPI.post(TASK_CONFIG_PATH, { task_id, schedules: schedulesPayload(schedules) });
    commit('setTaskConfig', response.data.data);
    return response.data.data;
  },

  async fetchAvailableTasks({ commit }) {
    commit('setIsLoadingTasks', true);
    try {
      const response = await dchAPI.get('tasks');
      commit('setAvailableTasks', response.data.data);
      return response.data.data;
    } catch (error) {
      throw error;
    } finally {
      commit('setIsLoadingTasks', false);
    }
  },

  async startTask({ commit }, { task_id, parameters, run_async = true }) {
    try {
      const response = await dchAPI.post('tasks/runs', {
        task_id,
        parameters,
        run_async,
      });
      const taskRun = response.data.data;
      commit('addTaskRun', taskRun);
      if (!run_async) {
        commit('setActiveRun', taskRun);
      }
      return taskRun;
    } catch (error) {
      throw error;
    }
  },

  async fetchTaskRun({ commit }, taskRunId) {
    try {
      const response = await dchAPI.get(`tasks/runs/${taskRunId}`);
      const taskRun = response.data.data;
      commit('updateTaskRun', taskRun);
      return taskRun;
    } catch (error) {
      throw error;
    }
  },

  async fetchTaskRuns({ commit, state }, { task_id = null, limit = 20, cursor = null } = {}) {
    commit('setIsLoadingRuns', true);
    try {
      const params = {};
      if (task_id) params.task_id = task_id;
      if (limit) params.limit = limit;
      if (cursor) params.cursor = cursor;

      const response = await dchAPI.get('tasks/runs', { params });
      const runs = response.data.data;
      const newCursor = response.data.meta?.cursor || null;

      if (cursor) {
        // Append to existing runs when loading more
        commit('appendTaskRuns', runs);
      } else {
        // Replace runs when filtering or initial load
        commit('setTaskRuns', runs);
      }
      commit('setRunsCursor', newCursor);
      return runs;
    } catch (error) {
      throw error;
    } finally {
      commit('setIsLoadingRuns', false);
    }
  },

  pollTaskRun({ dispatch }, { taskRunId, interval = 2000, maxAttempts = 100 }) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const poll = async () => {
        try {
          const taskRun = await dispatch('fetchTaskRun', taskRunId);
          attempts++;

          // Check if task is complete (you may need to adjust based on actual API response)
          if (taskRun.status === 'completed' || taskRun.status === 'failed' || taskRun.error) {
            resolve(taskRun);
            return;
          }

          if (attempts >= maxAttempts) {
            reject(new Error('Polling timeout'));
            return;
          }

          setTimeout(poll, interval);
        } catch (error) {
          reject(error);
        }
      };
      poll();
    });
  },

  clearActiveRun({ commit }) {
    commit('setActiveRun', null);
  },
};

const mutations = {
  setTaskConfigs(state, configs) {
    state.taskConfigs = configs;
  },
  setTaskConfig(state, config) {
    const others = state.taskConfigs.filter((c) => c.task_id !== config.task_id);
    state.taskConfigs = [...others, config].sort((a, b) => a.task_id.localeCompare(b.task_id));
  },
  removeTaskConfig(state, taskId) {
    state.taskConfigs = state.taskConfigs.filter((c) => c.task_id !== taskId);
  },
  setAvailableTasks(state, tasks) {
    state.availableTasks = tasks;
  },
  setTaskRuns(state, runs) {
    state.taskRuns = runs;
  },
  appendTaskRuns(state, runs) {
    state.taskRuns = [...state.taskRuns, ...runs];
  },
  addTaskRun(state, run) {
    // Add to beginning of runs array
    state.taskRuns = [run, ...state.taskRuns];
  },
  updateTaskRun(state, updatedRun) {
    const index = state.taskRuns.findIndex((run) => run.run_id === updatedRun.run_id);
    if (index !== -1) {
      state.taskRuns[index] = updatedRun;
    }
  },
  setActiveRun(state, run) {
    state.activeRun = run;
  },
  setIsLoadingTasks(state, isLoading) {
    state.isLoadingTasks = isLoading;
  },
  setIsLoadingRuns(state, isLoading) {
    state.isLoadingRuns = isLoading;
  },
  setRunsCursor(state, cursor) {
    state.runsCursor = cursor;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
